import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CoverSlide } from './slides/CoverSlide';
import { AboutSlide } from './slides/AboutSlide';
import { GapSlide } from './slides/GapSlide';
import { ChallengeSlide } from './slides/ChallengeSlide';
import { SolutionSlide } from './slides/SolutionSlide';
import { EcosystemSlide } from './slides/EcosystemSlide';
import { PlaceholderSlide } from './slides/PlaceholderSlide';
import { OnboardingSlide } from './slides/OnboardingSlide';
import { DriverOnboardingSlide } from './slides/DriverOnboardingSlide';
import { VehicleRegistrationSlide } from './slides/VehicleRegistrationSlide';
import { FleetRestrictionSlide } from './slides/FleetRestrictionSlide';
import { ValueSlide } from './slides/ValueSlide';
import { ImpactSlide } from './slides/ImpactSlide';
import { ClosingSlide } from './slides/ClosingSlide';
import { IndexSlide } from './slides/IndexSlide';
type SlideEntry = {
  id: string;
  label: string;
  render: () => React.ReactElement;
};
const SLIDES: SlideEntry[] = [{
  id: 'cover',
  label: 'Cover',
  render: () => <CoverSlide data-id="element-100" />
}, {
  id: 'index',
  label: 'Index',
  render: () => <IndexSlide data-id="element-101" />
}, {
  id: 'about',
  label: 'About',
  render: () => <AboutSlide data-id="element-102" />
}, {
  id: 'gap',
  label: 'Current Gap',
  render: () => <GapSlide data-id="element-103" />
}, {
  id: 'challenge',
  label: 'Challenge',
  render: () => <ChallengeSlide data-id="element-104" />
}, {
  id: 'solution',
  label: 'Solution',
  render: () => <SolutionSlide data-id="element-105" />
}, {
  id: 'ecosystem',
  label: 'Ecosystem',
  render: () => <EcosystemSlide data-id="element-106" />
}, {
  id: 'onboarding',
  label: 'Onboarding',
  render: () => <OnboardingSlide data-id="element-107" />
}, {
  id: 'driver-onboarding',
  label: 'Driver Onboarding',
  render: () => <DriverOnboardingSlide data-id="element-108" />
}, {
  id: 'vehicle-registration',
  label: 'Vehicle Registration',
  render: () => <VehicleRegistrationSlide data-id="element-109" />
}, {
  id: 'fleet-restriction',
  label: 'Fleet Restriction',
  render: () => <FleetRestrictionSlide data-id="element-110" />
}, {
  id: 'value',
  label: 'Value Proposition',
  render: () => <ValueSlide data-id="element-111" />
}, {
  id: 'impact',
  label: 'Impact',
  render: () => <ImpactSlide data-id="element-112" />
}, {
  id: 'closing',
  label: 'Closing',
  render: () => <ClosingSlide data-id="element-113" />
}];
export function SlideDeck() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const go = useCallback((next: number) => {
    const clamped = Math.max(0, Math.min(SLIDES.length - 1, next));
    setDirection(clamped > index ? 1 : -1);
    setIndex(clamped);
  }, [index]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') go(index + 1);else if (e.key === 'ArrowLeft') go(index - 1);else if (e.key === 'Home') go(0);else if (e.key === 'End') go(SLIDES.length - 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [go, index]);
  const current = SLIDES[index];
  return <div className="relative w-screen h-screen overflow-hidden" style={{
    background: '#EDF1F4'
  }} data-id="element-114">
      <AnimatePresence mode="popLayout" custom={direction} data-id="element-115">
        <motion.div key={current.id} custom={direction} initial={{
        opacity: 0,
        x: direction * 30
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: direction * -30
      }} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute inset-0" data-id="element-116">
          {current.render()}
        </motion.div>
      </AnimatePresence>

      <DeckChrome index={index} total={SLIDES.length} onGo={go} data-id="element-117" />
    </div>;
}
function DeckChrome({
  index,
  total,
  onGo
}: {
  index: number;
  total: number;
  onGo: (n: number) => void;
}) {
  return <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 pointer-events-auto" data-id="element-118">
      <div className="flex items-center gap-2" role="tablist" aria-label="Slide progress" data-id="element-119">
        {Array.from({
        length: total
      }).map((_, i) => {
        const active = i === index;
        return <button key={i} type="button" role="tab" aria-selected={active} aria-label={`Go to slide ${i + 1}`} onClick={() => onGo(i)} className="relative h-[3px] rounded-full overflow-hidden transition-all" style={{
          width: active ? 36 : 14,
          background: active ? 'var(--brand-teal)' : 'rgba(201,211,226,0.22)',
          boxShadow: active ? '0 0 14px rgba(53,192,200,0.7)' : 'none'
        }} data-id="element-120" />;
      })}
      </div>

      <div className="font-editorial italic tracking-[0.3em] text-xs pointer-events-none" style={{
      color: 'var(--ink-faint)'
    }} dir="ltr" data-id="element-121">
        {String(index + 1).padStart(2, '0')}
        <span className="mx-1" data-id="element-122">/</span>
        {String(total).padStart(2, '0')}
      </div>
    </div>;
}